import { useState, useEffect, useCallback } from "react";
import { dirOf } from "../utils/textDir";
import { createPortal } from "react-dom";
import { T, UI, PILL, FONT } from "../constants/tokens";
import { fetchMarketNews } from "../utils/finnhubNews";
import { fetchGeoEvents, classifyNewArticles, sortByOverlap, fetchLastScan, overlapTag, isOfficial } from "../utils/geoEvents";
import { useHoldingMarks, markFor } from "../utils/holdingSegments";
import { quotedHeadline } from "../utils/voice";
import { timeAgoIso } from "../utils/timeAgo";
import LTR from "./LTR";
import { regionLabelHe } from "../constants/regions";
import Icon from "./Icon";
import EmptyState from "./EmptyState";
import ScanStatusDot from "./ScanStatusDot";
import SourceQuote from "./SourceQuote";
import OfficialBadge from "./OfficialBadge";
import PortfolioTie from "./PortfolioTie";
import CardKind from "./CardKind";
import { modalScrim, modalPanel } from "./Modal";
import { Skeleton as Skel, Loading } from "./Skeleton";

// ─── Geopolitical intelligence — lives inside the News screen ────────────────
// Pipeline (all cache-first, AI runs at most once per article):
//   1. Read classified events from geo_events (last 48h).
//   2. Fetch the general Finnhub feed (5-min in-memory cache, no extra cost).
//   3. Send only articles the table doesn't know AND that overlap this user's
//      holdings (ticker or live sector — utils/geoCandidates.js, the cron's
//      rule) to the geoclassify function (hard 30/day cap on the client; the
//      function dedupes by source_url).
//   4. Re-read the table, drop category='none', overlap + sort client-side.
// Rows also arrive from the daily Federal Register cron (source_type
// 'official'); they are marked "מסמך רשמי" and sort ahead of news within the
// same overlap level. Nothing on this screen triggers that cron.



// Direct overlap and sector overlap must not read alike. "רלוונטי לסקטור X
// בתיק שלך" on an article about a company the user does not own suggested they
// owned it; the sector line now names which of their symbols carries the sector
// and which company the article is about. Watchlist symbols are also separated
// from holdings, since "החזקות" was counting both.
function contextLine(overlap) {
  if (overlap.level === "hot") {
    const held = overlap.heldSymbols;
    const watched = overlap.watchedSymbols;
    return (
      <>
        {held.length > 0 && <PortfolioTie symbols={held} weight={overlap.weightPct > 0 ? overlap.weightPct : null}/>}
        {watched.length > 0 && <PortfolioTie symbols={watched} held={false} style={{ marginTop: held.length ? 3 : 0 }}/>}
      </>
    );
  }
  if (overlap.level === "geo") {
    const subject = overlap.otherTickers[0];
    return (
      <>
        <PortfolioTie symbols={overlap.sectorSymbols} relation="sector"/>
        {subject && (
          <div style={{ marginTop: 3, fontWeight: 400 }}>
            הכתבה עוסקת ב־<LTR>{subject}</LTR>, שאינה בתיק שלך
          </div>
        )}
      </>
    );
  }
  return <>אין חפיפה להחזקות הנוכחיות שלך</>;
}

// Second line, for a direct overlap with a holding the user marked: what they
// marked and the share it had in the filing they marked it from. Two stored
// facts and nothing else — no claim that the event touches that segment; the
// reader has the segment and the headline and draws the line themselves.
// Nothing renders when no matched holding carries a real mark.
function markedLine(overlap, marks) {
  if (!overlap || overlap.level !== "hot") return null;
  const hits = overlap.heldSymbols
    .map(sym => ({ sym, mark: markFor(marks, sym) }))
    .filter(h => h.mark);
  if (!hits.length) return null;
  const { sym, mark } = hits[0];
  return (
    <div style={{ fontSize: 13, color: UI.text.secondary, marginTop: 6, lineHeight: 1.6, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
      <Icon name="target" size={13} color={UI.text.muted}/>
      <span>
        סימנת ב-<LTR>{sym}</LTR>: <span style={{ fontWeight: 700, color: UI.text.primary }}>{regionLabelHe(mark.segment_name) || <LTR style={{ unicodeBidi: "isolate" }}>{mark.segment_name}</LTR>}</span>
        {" · "}<LTR>{mark.marked_pct}%</LTR> מההכנסות שהחברה פילחה
        {hits.length > 1 && <> · ועוד <LTR>{hits.length - 1}</LTR></>}
      </span>
    </div>
  );
}

// Navigate to a stock page — same global mechanism used across the app.
const openTicker = (tk) =>
  window.dispatchEvent(new CustomEvent("si-open-stock", { detail: { symbol: tk } }));

function TickerPill({ tk }) {
  return (
    <button
      onClick={(e) => { e.stopPropagation(); openTicker(tk); }}
      style={{ ...PILL.base, ...PILL.neutral, fontFamily: FONT.latin, cursor: "pointer", minHeight: 28 }}>
      <span dir="ltr" style={{ unicodeBidi: "isolate" }}>{tk}</span>
    </button>
  );
}

// ─── Event detail modal ──────────────────────────────────────────────────────
// Opens when an event card is tapped — mirrors the news ArticleModal layout
// (source · time, headline, summary, portfolio context). Text only — no link
// out. No API calls: it renders data the card already holds.
function GeoDetailModal({ ev, overlap, marks, onClose }) {
  const tag = overlapTag(overlap);
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  return createPortal(
    <div
      onClick={onClose}
      style={modalScrim(1000)}>
      <div
        dir="rtl"
        onClick={(e) => e.stopPropagation()}
        style={{ ...modalPanel({ width: 640 }), position: "relative", gap: 12 }}>
        <button
          onClick={onClose}
          aria-label="סגור"
          style={{ position: "absolute", top: 10, left: 10, background: UI.surface.overlay, border: `1px solid ${UI.line.hairline}`, borderRadius: 6, width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: UI.text.primary, padding: 0 }}><Icon name="x" size={14} color={UI.text.primary}/></button>

        {/* The card's kind at its head: a Federal Register document is one
            kind of card, a classified news event another. The overlap tag
            stays beside it. */}
        <CardKind kind={isOfficial(ev) ? "official" : "geo"} trailing={tag && <span style={{ ...PILL.base, ...tag.style }}>{tag.label}</span>}/>

        {/* The same quotation the home card shows, rendered the same way — the
            source and the time now sit inside it, which is why they are gone
            from the row above. */}
        <SourceQuote
          quote={quotedHeadline(ev)}
          source={ev.source_name}
          when={timeAgoIso(ev.published_at || ev.created_at)}
          size={18}
        />

        {/* No English title under the Hebrew one (26/10/2026): the app shows
            no English headline. The source and time are in the quotation. */}

        {ev.summary_he && (
          <div dir={dirOf(ev.summary_he)} style={{ fontSize: 15, color: UI.text.secondary, lineHeight: 1.8 }}>{ev.summary_he}</div>
        )}

        {ev.tickers?.length > 0 && (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {ev.tickers.slice(0, 8).map(tk => <TickerPill key={tk} tk={tk}/>)}
          </div>
        )}

        <div style={{ fontSize: 13, paddingTop: 10, borderTop: `1px solid ${UI.line.hairline}`, color: overlap.level === "hot" ? UI.text.primary : overlap.level === "geo" ? UI.text.secondary : UI.text.muted, fontWeight: 600 }}>
          {contextLine(overlap)}
          {markedLine(overlap, marks)}
        </div>

        {/* No link out to the original article — the app never navigates away.
            The source name itself is already shown in the header row above. */}
      </div>
    </div>,
    document.body
  );
}

// `overlapOnly` (sprint 21, the market intelligence page): only the events
// that touch the portfolio, directly or by sector, and nothing at all · no
// skeleton, no empty state · when there are none. `onCount` hears how many
// are shown once the events are in, so the page can hide the section.
export default function GeoIntelTab({ portfolio = [], watchlist = [], focusUrl = null, onFocusHandled, overlapOnly = false, onCount }) {
  const [events,   setEvents]   = useState(null);   // null = loading
  const [failed,   setFailed]   = useState(false);
  const [selected, setSelected] = useState(null);   // { ev, overlap } when a card is open
  const [lastScan, setLastScan] = useState(null);   // ms of last scanner run, or null
  const { marks } = useHoldingMarks();              // the marking layer, for the second line

  // Cache-first: show what's already classified immediately (same as the
  // home-tab widget, which only ever reads geo_events — that's why it feels
  // instant while this tab used to feel "broken"/stuck). Classifying new
  // articles calls Gemini once per article with a deliberate 400ms gap
  // between calls (up to 30/day) — that can take tens of seconds, so it now
  // runs as a non-blocking background enrichment after the first paint
  // instead of gating it.
  const load = useCallback(async () => {
    setFailed(false);
    // Freshness is read independently of the events themselves: an empty list
    // and a stale scanner are different findings, and the empty state is
    // exactly where the user cannot tell them apart without this.
    fetchLastScan().then(setLastScan).catch(() => setLastScan(null));
    try {
      const existing = await fetchGeoEvents();
      setEvents(existing.filter(e => e.category && e.category !== "none"));
    } catch (e) {
      console.warn("[GeoIntel] load failed:", e?.message);
      setFailed(true);
      setEvents([]);
      return;
    }

    try {
      // Every stored row counts as classified, with or without its Hebrew
      // headline · or a row still waiting for it would be sent again on every
      // visit (review, 26/10/2026).
      const [existing, articles] = await Promise.all([fetchGeoEvents({ all: true }), fetchMarketNews()]);
      const known = new Set(existing.map(e => e.source_url));
      const sent = await classifyNewArticles(articles, known, portfolio, watchlist);
      if (sent > 0) {
        const all = await fetchGeoEvents();
        setEvents(all.filter(e => e.category && e.category !== "none"));
      }
    } catch (e) {
      // Background enrichment failing is not fatal — the events already
      // shown above (from the cache-first read) stay visible either way.
      console.warn("[GeoIntel] background classify failed:", e?.message);
    }
    // Keyed on the symbol lists, not the arrays: a price refresh replaces the
    // array objects every 30s and must not re-run the load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolio.map(p => p.symbol).join(","), watchlist.map(w => w.symbol).join(",")]);

  useEffect(() => { load(); }, [load]);

  const all = Array.isArray(events) ? sortByOverlap(events, portfolio, watchlist) : [];
  const sorted = overlapOnly ? all.filter(({ overlap }) => overlap.level !== "ok") : all;
  useEffect(() => {
    if (events !== null) onCount?.(failed ? 0 : sorted.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, failed, sorted.length]);

  // Open the card the home screen pointed at, once the events are actually in.
  // A url that isn't in the list — filtered out, aged past the 48h window, or
  // never classified — leaves the plain list showing, which is what this screen
  // did before. Either way the request is cleared so it fires only once.
  useEffect(() => {
    if (!focusUrl || !Array.isArray(events)) return;
    // From all events: the home screen may point at one with no overlap.
    const match = all.find(({ ev }) => ev.source_url === focusUrl);
    if (match) setSelected(match);
    onFocusHandled?.();
    // `sorted` is derived from `events` on every render; keying on events is
    // what actually matters here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusUrl, events]);

  if (overlapOnly && !sorted.length) {
    return selected ? <GeoDetailModal ev={selected.ev} overlap={selected.overlap} marks={marks} onClose={() => setSelected(null)}/> : null;
  }

  return (
    <div className="si-fade-in" style={{ display: "flex", flexDirection: "column", gap: 10 }}>

      {/* Scanner freshness — shown while still loading events too, since the
          two are independent reads and this one answers "is the engine alive". */}
      <ScanStatusDot lastScan={lastScan} withLabel/>

      {/* Loading skeletons */}
      {events === null && (
        <Loading>{[1, 2, 3].map(i => (
          <div key={i} style={{ background: UI.surface.raised, border: `1px solid ${UI.line.hairline}`, borderRadius: T.radius.xl, boxShadow: UI.shadow.card, padding: "14px 16px" }}>
            <Skel w="25%" h={20} mb={10}/>
            <Skel w="90%" h={15} mb={8}/>
            <Skel w="70%" h={13}/>
          </div>
        ))}</Loading>
      )}

      {/* Error state */}
      {events !== null && failed && (
        <div style={{ background: UI.surface.raised, border: `1px solid ${UI.line.hairline}`, borderRadius: T.radius.xl, boxShadow: UI.shadow.card, padding: "22px 16px", textAlign: "center" }}>
          <div style={{ fontSize: 14, color: UI.text.secondary, marginBottom: 12 }}>לא הצלחנו לטעון כרגע · נסה שוב</div>
          <button onClick={() => { setEvents(null); load(); }}
            style={{ background: UI.accent.soft, border: `1px solid ${UI.accent.hairline}`, borderRadius: T.radius.md, padding: "10px 22px", minHeight: 44, fontSize: 14, fontWeight: 700, color: UI.accent.base, cursor: "pointer", fontFamily: "inherit" }}>
            נסה שוב
          </button>
        </div>
      )}

      {/* Empty state */}
      {events !== null && !failed && sorted.length === 0 && (
        <EmptyState framed icon="globe" line="אין אירועים גיאופוליטיים מסווגים כרגע"/>
      )}

      {/* Event cards */}
      {sorted.map(({ ev, overlap }) => {
        const tag = overlapTag(overlap);
        return (
        <div key={ev.source_url}
          onClick={() => setSelected({ ev, overlap })}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelected({ ev, overlap }); } }}
          style={{
            background: UI.surface.raised,
            border: `1px solid ${overlap.level === "hot" ? UI.line.strong : UI.line.hairline}`,
            borderRadius: T.radius.lg,
            padding: "14px 16px",
            cursor: "pointer",
          }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
            {/* Provenance first, then relevance: a reader sees "this is the
                document itself" before "and it touches your holdings". */}
            <OfficialBadge ev={ev}/>
            {tag && <span style={{ ...PILL.base, ...tag.style }}>{tag.label}</span>}
            {ev.tickers?.length > 0 && (
              <span style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                {ev.tickers.slice(0, 4).map(tk => <TickerPill key={tk} tk={tk}/>)}
              </span>
            )}
          </div>

          {/* The same quotation the reading window opens with · Hebrew when
              a translation exists, in both places (ROADMAP, 2026-09-24). The
              source line under it is the attribution. */}
          {quotedHeadline(ev) && (
            <div dir={dirOf(quotedHeadline(ev).text)} style={{ fontSize: 15, fontWeight: 700, color: UI.text.primary, lineHeight: 1.5, fontFamily: FONT.heading, unicodeBidi: "isolate" }}>
              {quotedHeadline(ev).text}
            </div>
          )}

          {/* The English title that stood under the Hebrew one is gone
              (26/10/2026: no English headline). The source is in the card's
              own source line. */}

          {ev.summary_he && (
            <div dir={dirOf(ev.summary_he)} style={{ fontSize: 14, color: UI.text.secondary, lineHeight: 1.7, marginTop: 6 }}>
              {ev.summary_he}
            </div>
          )}

          <div style={{ fontSize: 12, color: UI.text.muted, marginTop: 8, display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
            {ev.source_name && <LTR>{ev.source_name}</LTR>}
            {ev.source_name && <span>·</span>}
            <span>{timeAgoIso(ev.published_at || ev.created_at)}</span>
          </div>

          <div style={{
            fontSize: 13, marginTop: 10, paddingTop: 10,
            borderTop: `1px solid ${UI.line.hairline}`,
            color: overlap.level === "hot" ? UI.text.primary : overlap.level === "geo" ? UI.text.secondary : UI.text.muted,
            fontWeight: 600,
          }}>
            {contextLine(overlap)}
            {markedLine(overlap, marks)}
          </div>
        </div>
        );
      })}

      {/* Event detail modal */}
      {selected && (
        <GeoDetailModal ev={selected.ev} overlap={selected.overlap} marks={marks} onClose={() => setSelected(null)}/>
      )}
    </div>
  );
}
